// @flow

import Collection from '../Collection';
import QueryParameters from '../../constants/Url/QueryParameters';

export type Filter = {
  id: string;
  name: string;
  group: string;
}

export default class Url {
  searchParameters: URLSearchParams;
  keys: Collection<string>;

  /**
   * Create a new URL class
   */
  constructor() {
    this.searchParameters = new URLSearchParams(window.location.search);
    this.keys = new Collection(Object.values(QueryParameters));
  }

  /**
   * Modifies the current url to the given url
   */
  update(url: string): void {
    window.history.pushState({ previousUrl: window.location.href }, '', url);
  }

  /**
   * Add a filter
   */
  addFilter(filter: Filter): void {
    if (this.containsFilter(filter)) {
      console.warn('Id already exists in the filters');
      return;
    }

    this.keys
      .reject((key: string) => [QueryParameters.ORDER_BY, QueryParameters.FILTERS].includes(key))
      .each((key: string) => {
        this.searchParameters.delete(key);
      });

    this.searchParameters.append(QueryParameters.FILTERS, filter.id);
    this.update(`${this.current()}?${this.searchParameters.toString()}`);
  }

  /**
   * Remove a filter
   */
  removeFilter(filter: Filter): void {
    const currentFilters = this.activeFilters();

    currentFilters.delete(filter.id);
    this.clearFilters();

    if (!currentFilters.count()) return;

    currentFilters.each((filter: string) => {
      this.searchParameters.append(QueryParameters.FILTERS, filter);
    });

    this.update(`${this.current()}?${this.searchParameters.toString()}`);
  }

  /**
   * Clear filters
   */
  clearFilters(): void {
    this.keys
      .reject((key: string) => key === QueryParameters.ORDER_BY)
      .each((key: string) => this.searchParameters.delete(key));

    if (!this.hasParameters()) {
      this.update(`${this.current()}`);
      return;
    }

    this.update(`${this.current()}?${this.searchParameters.toString()}`);
  }

  /**
   * Check if the URL contains the given filter
   */
  containsFilter(filter: Filter): boolean {
    return this.activeFilters().contains(filter.id);
  }

  /**
   * Get all the active filters
   */
  activeFilters(): Collection<string> {
    return new Collection<string>(this.searchParameters.getAll(QueryParameters.FILTERS) || []);
  }

  /**
   * Set the page
   */
  setPage(page: number): void {
    const firstPage: number = 1;

    if (page === firstPage) {
      this.removePage();
      return;
    }

    this.searchParameters.set(QueryParameters.PAGE, page);
    this.update(`${this.current()}?${this.searchParameters.toString()}`);
  }

  /**
   * Remove the page
   */
  removePage(): void {
    this.searchParameters.delete(QueryParameters.PAGE);

    if (!this.hasParameters()) {
      this.update(`${this.current()}`);
      return;
    }

    this.update(`${this.current()}?${this.searchParameters.toString()}`);
  }

  /**
   * Set a price filter
   */
  setPrice(min: number, max: number): void {
    this.searchParameters.delete(QueryParameters.PAGE);
    this.searchParameters.set(QueryParameters.PRICE, JSON.stringify([min, max]));
    this.update(`${this.current()}?${this.searchParameters.toString()}`);
  }

  /**
   * Remove the price filter
   */
  removePrice(): void {
    this.searchParameters.delete(QueryParameters.PRICE);

    if (!this.hasParameters()) {
      this.update(`${this.current()}`);
      return;
    }

    this.update(`${this.current()}?${this.searchParameters.toString()}`);
  }

  /**
   * Set the order by filter
   */
  setOrder(by: string): void {
    this.searchParameters.set(QueryParameters.ORDER_BY, by);
    this.update(`${this.current()}?${this.searchParameters.toString()}`);
  }

  /**
   * Returns the current url
   */
  current(): string {
    const location: Object = window.location;
    return location.protocol + '//' + location.host + location.pathname;
  }

  /**
   * Resets the url
   */
  reset(): void {
    this.update(this.current());
    this.refreshSearchParameters();
  }

  /**
   * Reset the search parameters
   */
  refreshSearchParameters(): void {
    this.searchParameters = new URLSearchParams(window.location.search);
  }

  /**
   * Check if the Url has any parameters (like filters, price, page etc..)
   */
  hasParameters(): boolean {
    return !this.searchParameters.entries().next().done;
  }
}
